import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import {
  Users, Search, ChevronLeft, ChevronRight, Loader2, KeyRound, ShieldCheck,
  Mail, CalendarClock, CreditCard, Ticket, Award,
} from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 25;
const ROLE_LABELS = {
  super_admin: "Super Admin", admin: "Admin", support: "Support", content_manager: "Content Mgr",
};

function accessBadge(u) {
  const now = new Date();
  if (u.access_type === "lifetime") return <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100">Lifetime</Badge>;
  if (u.access_type === "paid" && u.access_expires_at && new Date(u.access_expires_at) > now)
    return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Paid</Badge>;
  if (u.access_type === "paid") return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Expired</Badge>;
  return <Badge variant="secondary">Free</Badge>;
}

function fmtDate(d) { return d ? new Date(d).toLocaleDateString() : "—"; }

export default function UserManager() {
  const { user: me } = useAuth();
  const isSuper = (me?.admin_role || (me?.role === "admin" ? "super_admin" : "")) === "super_admin";

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [access, setAccess] = useState("all");
  const [role, setRole] = useState("all");
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [grantDays, setGrantDays] = useState(90);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", {
        params: { q: debouncedQ, access, role, page, page_size: PAGE_SIZE },
      });
      setRows(data.items); setTotal(data.total);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [debouncedQ, access, role, page]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (u) => {
    setSelected(u); setDetail(null); setDetailLoading(true); setGrantDays(90);
    try { const { data } = await api.get(`/admin/users/${u.id}`); setDetail(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDetailLoading(false); }
  };

  const refreshBoth = async (u) => {
    await load();
    const { data } = await api.get(`/admin/users/${u.id}`);
    setDetail(data);
  };

  const setAccessType = async (type) => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.put(`/admin/users/${selected.id}/access`, { access_type: type, days: grantDays });
      toast.success(type === "free" ? "Access revoked" : type === "lifetime" ? "Lifetime access granted" : `Granted ${grantDays} days`);
      await refreshBoth(selected);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendReset = async () => {
    if (!selected) return;
    setBusy(true);
    try { await api.post(`/admin/users/${selected.id}/send-reset`); toast.success("Password reset email sent"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setUserRole = async (newRole) => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.put(`/admin/users/${selected.id}/role`, { admin_role: newRole === "none" ? null : newRole });
      toast.success("Admin role updated");
      await refreshBoth(selected);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const du = detail?.user || selected || {};
  const currentRole = du.admin_role || (du.role === "admin" ? "super_admin" : "none");

  return (
    <div data-testid="admin-user-manager">
      <div className="flex items-center gap-2 mb-4">
        <Users className="h-5 w-5 text-amber-600" />
        <h3 className="font-heading font-semibold text-[#0A192F]">User Management</h3>
        <span className="text-xs text-slate-400">({total})</span>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="pl-9 h-9" data-testid="admin-user-search" />
        </div>
        <Select value={access} onValueChange={(v) => { setAccess(v); setPage(1); }}>
          <SelectTrigger className="w-40 h-9" data-testid="admin-user-access-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All access</SelectItem>
            <SelectItem value="active">Active paid</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="lifetime">Lifetime</SelectItem>
            <SelectItem value="free">Free</SelectItem>
          </SelectContent>
        </Select>
        <Select value={role} onValueChange={(v) => { setRole(v); setPage(1); }}>
          <SelectTrigger className="w-40 h-9" data-testid="admin-user-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="admins">Any admin</SelectItem>
            <SelectItem value="super_admin">Super Admin</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="support">Support</SelectItem>
            <SelectItem value="content_manager">Content Mgr</SelectItem>
            <SelectItem value="student">Students only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-user-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left font-medium px-4 py-2.5">User</th>
              <th className="text-left font-medium px-4 py-2.5 hidden sm:table-cell">Access</th>
              <th className="text-left font-medium px-4 py-2.5 hidden md:table-cell">Role</th>
              <th className="text-left font-medium px-4 py-2.5 hidden lg:table-cell">Expires</th>
              <th className="text-left font-medium px-4 py-2.5 hidden lg:table-cell">Joined</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No users match your filters.</td></tr>
            ) : rows.map((u) => (
              <tr key={u.id} onClick={() => openDetail(u)} className="hover:bg-amber-50/50 cursor-pointer" data-testid={`admin-user-row-${u.id}`}>
                <td className="px-4 py-2.5">
                  <div className="font-medium text-[#0A192F]">{u.name || "—"}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </td>
                <td className="px-4 py-2.5 hidden sm:table-cell">{accessBadge(u)}</td>
                <td className="px-4 py-2.5 hidden md:table-cell text-xs">
                  {u.admin_role || u.role === "admin" ? (
                    <Badge className="bg-[#0A192F] hover:bg-[#0A192F]">{ROLE_LABELS[u.admin_role] || "Super Admin"}</Badge>
                  ) : <span className="text-slate-400">Student</span>}
                </td>
                <td className="px-4 py-2.5 hidden lg:table-cell text-xs text-slate-500">{u.access_type === "lifetime" ? "Never" : fmtDate(u.access_expires_at)}</td>
                <td className="px-4 py-2.5 hidden lg:table-cell text-xs text-slate-500">{fmtDate(u.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3 text-sm">
        <span className="text-slate-500">Page {page} of {pages}</span>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="admin-user-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="admin-user-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setDetail(null); } }}>
        <DialogContent className="max-w-lg" data-testid="admin-user-detail-dialog">
          <DialogHeader><DialogTitle>{du.name || du.email}</DialogTitle></DialogHeader>
          {detailLoading ? (
            <div className="py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : (
            <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
              <div className="rounded-lg border border-slate-200 p-3 text-sm space-y-1.5">
                <div className="flex justify-between"><span className="text-slate-500 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email</span><span className="text-[#0A192F]">{du.email}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Access</span><span>{accessBadge(du)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500 flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Expires</span><span className="text-[#0A192F]">{du.access_type === "lifetime" ? "Never" : fmtDate(du.access_expires_at)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Joined</span><span className="text-[#0A192F]">{fmtDate(du.created_at)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Email verified</span><span className="text-[#0A192F]">{du.email_verified ? "Yes" : "No"}</span></div>
                {detail && <div className="flex justify-between"><span className="text-slate-500 flex items-center gap-1.5"><Award className="h-3.5 w-3.5" /> Quiz attempts</span><span className="text-[#0A192F]">{detail.quiz_attempts}</span></div>}
              </div>

              <div>
                <Label className="text-xs text-slate-500">Access controls</Label>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <div className="flex items-center gap-1.5">
                    <Input type="number" min={1} value={grantDays} onChange={(e) => setGrantDays(Number(e.target.value))} className="w-20 h-9" data-testid="admin-grant-days" />
                    <Button size="sm" disabled={busy} onClick={() => setAccessType("paid")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="admin-grant-paid">Grant days</Button>
                  </div>
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => setAccessType("lifetime")} data-testid="admin-grant-lifetime">Lifetime</Button>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" disabled={busy} onClick={() => setAccessType("free")} data-testid="admin-revoke-access">Revoke</Button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" disabled={busy} onClick={sendReset} data-testid="admin-send-reset"><KeyRound className="h-4 w-4 mr-1.5" /> Send password reset</Button>
              </div>

              {isSuper && (
                <div>
                  <Label className="text-xs text-slate-500 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Admin role</Label>
                  <Select value={currentRole} onValueChange={setUserRole} disabled={busy}>
                    <SelectTrigger className="mt-1.5 h-9" data-testid="admin-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No admin access (Student)</SelectItem>
                      <SelectItem value="content_manager">Content Manager</SelectItem>
                      <SelectItem value="support">Support</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {detail && detail.payments.length > 0 && (
                <div>
                  <Label className="text-xs text-slate-500 flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5" /> Payments</Label>
                  <div className="mt-1.5 rounded-lg border border-slate-200 divide-y text-sm">
                    {detail.payments.map((p) => (
                      <div key={p.id} className="px-3 py-2 flex justify-between">
                        <span className="text-[#0A192F]">${(p.amount || 0).toFixed(2)}{p.coupon_code ? ` · ${p.coupon_code}` : ""}</span>
                        <span className="text-xs text-slate-400">{p.payment_status || "—"} · {fmtDate(p.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detail && detail.redemptions.length > 0 && (
                <div>
                  <Label className="text-xs text-slate-500 flex items-center gap-1.5"><Ticket className="h-3.5 w-3.5" /> Code redemptions</Label>
                  <div className="mt-1.5 rounded-lg border border-slate-200 divide-y text-sm">
                    {detail.redemptions.map((r) => (
                      <div key={r.id} className="px-3 py-2 flex justify-between">
                        <span className="font-mono text-[#0A192F]">{r.coupon_code || r.code || "—"}</span>
                        <span className="text-xs text-slate-400">{fmtDate(r.created_at || r.at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => { setSelected(null); setDetail(null); }}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
